import { ComputedRef } from 'vue'

import { CommentaryInfo } from '@/domain/value_objects/commentary/CommentaryInfo'
import { DisplayLanguageType } from '@/domain/value_objects/language/displayLanguageType'
import { ProgramLanguageType } from '@/domain/value_objects/language/programLanguageType'
import { CommentaryPageStateViewModel } from '@/presentation/viewmodels/commentary/commentaryPageStateViewModel'
import { GetCommentaryViewModel } from '@/presentation/viewmodels/commentary/getCommentaryViewModel'

export class CommentaryPageStatePresenter {
  constructor(
    private readonly viewModel: CommentaryPageStateViewModel,
    private readonly getCommentaryViewModel: GetCommentaryViewModel = new GetCommentaryViewModel()
  ) {}

  get isPreCommentaryFirstTimeOpendPage(): ComputedRef<boolean> {
    return this.viewModel.isPreCommentaryFirstTimeOpendPage
  }

  get isPostCommentaryFirstTimeOpendPage(): ComputedRef<boolean> {
    return this.viewModel.isPostCommentaryFirstTimeOpendPage
  }

  get preCommentarySelectedList(): ComputedRef<{ [type: string]: number }> {
    return this.viewModel.preCommentarySelectedList
  }

  get postCommentarySelectedList(): ComputedRef<{ [type: string]: number }> {
    return this.viewModel.postCommentarySelectedList
  }

  get preCommentaryList(): ComputedRef<{ [type: string]: { [filter: string]: string } }> {
    return this.viewModel.preCommentaryList
  }

  get postCommentaryList(): ComputedRef<{ [type: string]: { [filter: string]: string } }> {
    return this.viewModel.postCommentaryList
  }

  openCommentaryPage(
    programLanguage: ProgramLanguageType,
    displayLanguage: DisplayLanguageType,
    problemId: string,
    pattern: string,
    list: CommentaryInfo[]
  ): void {
    const isFirstTime =
      pattern === 'pre' ? this.isPreCommentaryFirstTimeOpendPage.value : this.isPostCommentaryFirstTimeOpendPage.value

    if (isFirstTime) {
      this.handleFirstTimeOpening(programLanguage, displayLanguage, problemId, pattern, list)
    } else {
      this.handleSubsequentOpening(displayLanguage, problemId, pattern, list)
    }

    this.updateFirstTimeFlag(pattern)
  }

  private updateFirstTimeFlag(pattern: string): void {
    if (pattern === 'pre') {
      this.viewModel.setIsPreCommentaryFirstTimeOpendPage(false)
    } else if (pattern === 'post') {
      this.viewModel.setIsPostCommentaryFirstTimeOpendPage(false)
    }
  }

  private handleFirstTimeOpening(
    programLanguage: ProgramLanguageType,
    displayLanguage: DisplayLanguageType,
    problemId: string,
    pattern: string,
    list: CommentaryInfo[]
  ): void {
    list.forEach(commentaryInfo => {
      const { type, filter } = commentaryInfo
      const matchingFilterIndex = filter.findIndex(f => f === programLanguage)

      if (matchingFilterIndex !== -1) {
        this.bindCommentary(displayLanguage, problemId, pattern, type, filter[matchingFilterIndex], matchingFilterIndex)
      } else {
        this.bindCommentary(displayLanguage, problemId, pattern, type, filter[0], 0)
      }
    })
  }

  private handleSubsequentOpening(
    displayLanguage: DisplayLanguageType,
    problemId: string,
    pattern: string,
    list: CommentaryInfo[]
  ): void {
    const selectedList =
      pattern === 'pre' ? this.preCommentarySelectedList.value : this.postCommentarySelectedList.value

    list.forEach(commentaryInfo => {
      const { type, filter } = commentaryInfo
      const selectedIndex = selectedList[type]
      const selectedFilter = filter[selectedIndex]

      this.bindCommentary(displayLanguage, problemId, pattern, type, selectedFilter, selectedIndex)
    })
  }

  async bindCommentary(
    displayLanguage: string,
    problemId: string,
    pattern: string,
    type: string,
    filter: string,
    selectedIndex: number
  ): Promise<void> {
    await this.updateCommentarySelection(pattern, type, selectedIndex)
    await this.updateCommentaryContent(displayLanguage, problemId, pattern, type, filter)
  }

  private async updateCommentarySelection(pattern: string, type: string, selectedIndex: number): Promise<void> {
    if (pattern === 'pre') {
      await this.viewModel.setPreCommentarySelected(type, selectedIndex)
    } else if (pattern === 'post') {
      await this.viewModel.setPostCommentarySelected(type, selectedIndex)
    }
  }

  private async updateCommentaryContent(
    displayLanguage: string,
    problemId: string,
    pattern: string,
    type: string,
    filter: string
  ): Promise<void> {
    const html = await this.getCommentaryViewModel.getCommentary(displayLanguage, problemId, pattern, type, filter)
    const div = document.createElement('div')
    div.innerHTML = html

    const targetElement = document.getElementById(`${pattern}:${type}`)
    if (!targetElement) return

    this.clearElement(targetElement)
    targetElement.appendChild(div)
  }

  private clearElement(element: HTMLElement): void {
    while (element.firstChild) {
      element.removeChild(element.firstChild)
    }
  }
}
